import { PropertyTypeSelect } from "./propertyType/select";
import { PropertyTypeText } from "./propertyType/text";

export interface PropertyType {
  "prop-type": PropertyTypeKey;
  name: string;
  "folder-id": string;
  type: "omega/query-omega/data/database/property/Property";
}

export interface GetFolderPropertyTypesResponse {
  "app-id": string;
  "folder-id": string;
  properties: PropertyTypesMap;
}

export type PropertyValue = any;

/**
 * A stored property value as DISPLAY TEXT — the one rule every renderer uses.
 *
 * Only `undefined`/`null` mean "no value": a stored `0` or `false` is a real value, because
 * the engine round-trips JSON types verbatim (query-omega-oql
 * `oql/app/database/property.oql:245-257` accepts any value for a text column). A truthiness
 * test drops those, which is the bug this function exists to make unrepeatable.
 *
 * The wrapped `{"prop-value": x}` form is unwrapped first. Nothing in this repo writes that
 * shape and the engine returns flat scalars, but three separate call sites defended against
 * it independently, so the tolerance is kept — in ONE place.
 *
 * Returns `""` for "no value". Callers decide how to show that: the "No Value" placeholder
 * belongs to DynamicTextField, and PropertyPill renders nothing at all.
 */
export const propValueToText = (raw: PropertyValue): string => {
  const value =
    raw !== null && typeof raw === "object" && "prop-value" in raw
      ? (raw as { "prop-value": unknown })["prop-value"]
      : raw;
  if (value === undefined || value === null) return "";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
};

export interface PropertyValuesMap {
  [name: string]: PropertyValue;
}

export interface PagePropertyValuesMap {
  [pageId: string]: PropertyValuesMap;
}

export interface GetPropertyValuesResponse {
  "app-id": string;
  "folder-id": string;
  "prop-values": PagePropertyValuesMap;
  props: FolderPropertyTypesMap;
}

// A group of folders with property types
export interface FolderPropertyTypesMap {
  [folderId: string]: PropertyTypesMap;
}

// Property Types For a Folder
export interface PropertyTypesMap {
  [propName: string]: PropertyType;
}

export enum PropertyTypeKey {
  TEXT = "text",
  SELECT = "select",
}

export const PropertyTypeDisplayName = {
  text: "Text",
  select: "Select",
};

export enum PropertyValueKey {
  TEXT = "omega/query-omega/page/property/PropertyValueText",
  SELECT = "omega/query-omega/page/property/PropertyValueSelect",
}

export const initialPropertyTypeValue: (
  key: PropertyTypeKey,
  folderId: string,
) => PropertyType = (key: PropertyTypeKey, folderId) => {
  switch (key) {
    case PropertyTypeKey.TEXT:
      const initValText: PropertyTypeText = {
        "prop-type": PropertyTypeKey.TEXT,
        type: "omega/query-omega/data/database/property/Property",
        name: "Text",
        "folder-id": folderId,
      };
      return initValText;
    case PropertyTypeKey.SELECT:
      const initValSelect: PropertyTypeSelect = {
        "prop-type": PropertyTypeKey.SELECT,
        type: "omega/query-omega/data/database/property/Property",
        name: "Select",
        options: [{ label: "Option 1" }, { label: "Option 2" }],
        "folder-id": folderId,
      };
      return initValSelect;
  }
};

export interface FolderPrimaryKeyMap {
  [folderId: string]: string[];
}

export interface FolderSecIndexesMap {
  [folderId: string]: string[];
}

export const RESERVED_PROPERTY_NAMES = ["page", "name"];

export const isReservedPropertyName = (name: string | undefined): boolean =>
  RESERVED_PROPERTY_NAMES.includes((name ?? "").trim().toLowerCase());
